import { useState, useCallback } from 'react';
import { Plus, Loader2, FileText, Trash2, ExternalLink, Download, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Modal, ConfirmDialog, InputDecimal } from '../ui';
import { useApiEffect } from '../../hooks';
import type { NfeVeiculo, NfeVeiculoCreate, NfeVeiculoResumo, TipoNfe, StatusNfe } from '../../types';
import { formatCurrency, formatDate } from '../../utils/format';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  veiculoId: number;
  veiculoNome: string;
}

const TIPOS_NFE: { value: TipoNfe; label: string }[] = [
  { value: 'ENTRADA', label: 'Entrada' },
  { value: 'SAIDA', label: 'Saída' },
  { value: 'TRANSFERENCIA', label: 'Transferência' },
];

const statusColors: Record<StatusNfe, string> = {
  EMITIDA: 'bg-green-100 text-green-700',
  CANCELADA: 'bg-red-100 text-red-700',
  SUBSTITUIDA: 'bg-yellow-100 text-yellow-700',
};

const statusIcons: Record<StatusNfe, typeof CheckCircle> = {
  EMITIDA: CheckCircle,
  CANCELADA: XCircle,
  SUBSTITUIDA: RefreshCw,
};

const emptyForm: NfeVeiculoCreate = {
  notaGoianaId: undefined,
  numero: '',
  serie: '1',
  tipo: 'SAIDA',
  chaveAcesso: '',
  valor: 0,
  dataEmissao: new Date().toISOString().split('T')[0],
  destinatarioNome: '',
  destinatarioCpfCnpj: '',
  xmlUrl: '',
  pdfUrl: '',
};

export function NfeVeiculoModal({ isOpen, onClose, veiculoId, veiculoNome }: Props) {
  const { showSuccess, showError, showApiError } = useNotification();

  const [nfes, setNfes] = useState<NfeVeiculo[]>([]);
  const [resumo, setResumo] = useState<NfeVeiculoResumo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<NfeVeiculoCreate>(emptyForm);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadData = useCallback(async (signal: AbortSignal) => {
    if (!veiculoId) return;
    setIsLoading(true);

    try {
      const [nfesRes, resumoRes] = await Promise.all([
        api.get<NfeVeiculo[]>(`/veiculos/${veiculoId}/nfe`, { signal }),
        api.get<NfeVeiculoResumo>(`/veiculos/${veiculoId}/nfe/resumo`, { signal }),
      ]);
      setNfes(nfesRes.data);
      setResumo(resumoRes.data);
    } finally {
      setIsLoading(false);
    }
  }, [veiculoId]);

  const { refetch } = useApiEffect(loadData, [veiculoId], { enabled: isOpen && !!veiculoId });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.numero.trim() || !form.dataEmissao || !form.valor) {
      showError('Número, data e valor são obrigatórios');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingId) {
        await api.put(`/veiculos/${veiculoId}/nfe/${editingId}`, { status: 'EMITIDA' });
        showSuccess('NFe atualizada');
      } else {
        await api.post(`/veiculos/${veiculoId}/nfe`, form);
        showSuccess('NFe cadastrada');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      refetch();
    } catch (error) {
      showApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await api.delete(`/veiculos/${veiculoId}/nfe/${deletingId}`);
      showSuccess('NFe removida');
      setShowDeleteDialog(false);
      setDeletingId(null);
      refetch();
    } catch (error) {
      showApiError(error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Notas Fiscais - ${veiculoNome}`} size="xl">
      <div className="space-y-4">
        {/* Resumo */}
        {resumo && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-500">Total de Notas</p>
              <p className="text-lg font-semibold">{resumo.totalNotas}</p>
            </div>
            <div>
              <p className="text-sm text-blue-600">Entrada</p>
              <p className="text-lg font-semibold text-blue-600">{resumo.notasEntrada}</p>
            </div>
            <div>
              <p className="text-sm text-green-600">Saída</p>
              <p className="text-lg font-semibold text-green-600">{resumo.notasSaida}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Valor Total Saída</p>
              <p className="text-lg font-semibold">{formatCurrency(resumo.valorTotalSaida)}</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500">
            Integração com NotaGoiana para emissão de notas
          </p>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingId(null);
              setForm(emptyForm);
            }}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <Plus className="w-4 h-4" />
            Vincular NFe
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número *</label>
                <input
                  type="text"
                  value={form.numero}
                  onChange={(e) => setForm({ ...form, numero: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="000001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Série</label>
                <input
                  type="text"
                  value={form.serie}
                  onChange={(e) => setForm({ ...form, serie: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value as TipoNfe })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {TIPOS_NFE.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Emissão *</label>
                <input
                  type="date"
                  value={form.dataEmissao}
                  onChange={(e) => setForm({ ...form, dataEmissao: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor *</label>
                <InputDecimal
                  value={form.valor}
                  onChange={(value) => setForm({ ...form, valor: value ?? 0 })}
                  prefix="R$ "
                  placeholder="0,00"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Chave de Acesso</label>
              <input
                type="text"
                value={form.chaveAcesso}
                onChange={(e) => setForm({ ...form, chaveAcesso: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="44 dígitos"
                maxLength={44}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Destinatário Nome</label>
                <input
                  type="text"
                  value={form.destinatarioNome}
                  onChange={(e) => setForm({ ...form, destinatarioNome: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Destinatário CPF/CNPJ</label>
                <input
                  type="text"
                  value={form.destinatarioCpfCnpj}
                  onChange={(e) => setForm({ ...form, destinatarioCpfCnpj: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL do XML</label>
                <input
                  type="url"
                  value={form.xmlUrl}
                  onChange={(e) => setForm({ ...form, xmlUrl: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL do PDF</label>
                <input
                  type="url"
                  value={form.pdfUrl}
                  onChange={(e) => setForm({ ...form, pdfUrl: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setForm(emptyForm);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Salvar
              </button>
            </div>
          </form>
        )}

        {/* List */}
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : nfes.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">Nenhuma nota fiscal vinculada</p>
            </div>
          ) : (
            nfes.map((nfe) => {
              const StatusIcon = statusIcons[nfe.status];

              return (
                <div
                  key={nfe.id}
                  className={`p-4 border rounded-lg ${
                    nfe.status === 'CANCELADA' ? 'bg-red-50 border-red-200' : 'bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <StatusIcon
                          className={`w-4 h-4 ${statusColors[nfe.status].replace('bg-', 'text-').replace('-100', '-600')}`}
                        />
                        <span className={`px-2 py-0.5 text-xs rounded font-medium ${statusColors[nfe.status]}`}>
                          {nfe.status}
                        </span>
                        <span className="font-medium text-gray-900">
                          NFe {nfe.numero}/{nfe.serie}
                        </span>
                        <span
                          className={`px-2 py-0.5 text-xs rounded ${
                            nfe.tipo === 'ENTRADA' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {TIPOS_NFE.find((t) => t.value === nfe.tipo)?.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span>Emissão: {formatDate(nfe.dataEmissao)}</span>
                        <span className="font-medium text-gray-700">{formatCurrency(nfe.valor)}</span>
                        {nfe.destinatarioNome && <span>Dest: {nfe.destinatarioNome}</span>}
                        {nfe.notaGoianaId && (
                          <span className="text-blue-600">NotaGoiana #{nfe.notaGoianaId}</span>
                        )}
                      </div>
                      {nfe.chaveAcesso && (
                        <p className="text-xs text-gray-400 font-mono mt-1 truncate">
                          Chave: {nfe.chaveAcesso}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {nfe.pdfUrl && (
                        <a
                          href={nfe.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      )}
                      {nfe.xmlUrl && (
                        <a
                          href={nfe.xmlUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="Download XML"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <button
                        onClick={() => {
                          setDeletingId(nfe.id);
                          setShowDeleteDialog(true);
                        }}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Remover"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        type="danger"
        title="Remover NFe"
        message="Tem certeza que deseja remover esta nota fiscal do veículo?"
        confirmText="Remover"
      />
    </Modal>
  );
}
